/**
 * SUNTRONIX ONE — "Discover Suntronix" slide-over.
 *
 * A sophisticated info panel that opens over the login without
 * navigating away.  ABOUT · SERVICES · LIFECYCLE · SUNTRONIX ONE ·
 * TECHNOLOGY ECOSYSTEM · CONTACT.
 */
import { useState } from "react";
import { X, Mail, MapPin, Globe, Phone } from "lucide-react";

const TABS = ["About", "Services", "Lifecycle", "SUNTRONIX ONE", "Ecosystem", "Contact"];

export default function DiscoverSuntronix({ onClose }) {
  const [tab, setTab] = useState("About");
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="discover-suntronix-panel">
      <div className="absolute inset-0 bg-[#050d1a]/70 backdrop-blur-sm"
              onClick={onClose} aria-hidden />
      <aside className="ml-auto relative bg-white w-full max-w-[560px] h-full overflow-y-auto shadow-[-24px_0_48px_-24px_rgba(6,12,32,0.55)]"
              role="dialog" aria-label="Discover Suntronix">
        <header className="sticky top-0 bg-white z-10 border-b border-[#E1E7F0] px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#8B95AB]">Discover</div>
            <div className="text-[18px] font-heading text-[#0D1B3D] tracking-tight">SUNTRONIX</div>
          </div>
          <button onClick={onClose} data-testid="discover-close"
                    className="h-8 w-8 rounded-full hover:bg-[#F0F4FA] grid place-items-center text-[#4A5470]">
            <X className="h-4 w-4" />
          </button>
        </header>

        <nav className="px-8 pt-4 flex flex-wrap gap-1 border-b border-[#E1E7F0]">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
                      data-testid={`discover-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`px-3 py-2 text-[12px] font-medium tracking-[-0.005em] rounded-t-md border-b-2 transition-colors ${
                          tab === t
                              ? "border-[#2962FF] text-[#0D1B3D]"
                              : "border-transparent text-[#4A5470] hover:text-[#0D1B3D]"}`}>
              {t}
            </button>
          ))}
        </nav>

        <div className="px-8 py-6 text-[14px] leading-relaxed text-[#1E2A44]">
          {tab === "About" && (
            <div className="space-y-5">
              <h3 className="font-heading text-[24px] tracking-[-0.015em] text-[#0D1B3D]">
                Pioneering enterprise technology since 1999.
              </h3>
              <p>Suntronix has evolved alongside workplace technology — from personal devices and emerging mobile technology to enterprise deployment, lifecycle services and global technology operations.</p>
              <p>Today, Suntronix helps organisations manage technology across the complete lifecycle while maintaining visibility, accountability and chain of custody.</p>
              <div className="grid grid-cols-2 gap-3 pt-3">
                <Fact label="Established" value="1999" />
                <Fact label="Head office" value="Chennai, India" />
                <Fact label="Delivery" value="Pan-India capability" />
                <Fact label="Focus" value="Enterprise technology operations" />
              </div>
            </div>
          )}
          {tab === "Services" && (
            <div className="space-y-4">
              {SERVICES.map((s) => (
                <div key={s.title}>
                  <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#2962FF]">{s.title}</div>
                  <div className="text-[13px] text-[#1E2A44] mt-1">{s.body}</div>
                </div>
              ))}
            </div>
          )}
          {tab === "Lifecycle" && (
            <div className="space-y-3">
              <h3 className="font-heading text-[20px] text-[#0D1B3D] tracking-[-0.01em]">Complete asset lifecycle</h3>
              <ol className="grid grid-cols-2 gap-2">
                {[
                  ["Procure",  "#2962FF"], ["Deploy",  "#00C8FF"],
                  ["Manage",   "#18D5A4"], ["Support", "#FFB84D"],
                  ["Recover",  "#7657FF"], ["Reuse",   "#18D5A4"],
                  ["Redeploy", "#2962FF"], ["Recycle", "#0F9F7A"],
                ].map(([label, colour]) => (
                  <li key={label} className="rounded-md border border-[#E1E7F0] px-3 py-2 flex items-center gap-2">
                    <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: colour }} />
                    <span className="text-[13px] font-medium text-[#0D1B3D]">{label}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {tab === "SUNTRONIX ONE" && (
            <div className="space-y-4">
              <h3 className="font-heading text-[24px] text-[#0D1B3D] tracking-[-0.015em]">
                Your technology estate. Managed as one.
              </h3>
              <p>SUNTRONIX ONE connects customers, employees, technology, operations, logistics, support, recovery, commercial workflows and lifecycle intelligence into one operating system.</p>
              {POWERED.map((p) => (
                <div key={p.title}>
                  <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#00C8FF]">{p.title}</div>
                  <div className="text-[13px] mt-1">{p.body}</div>
                </div>
              ))}
            </div>
          )}
          {tab === "Ecosystem" && (
            <div className="space-y-4">
              <p className="text-[13px] text-[#4A5470]">
                Multi-brand technology sourcing and lifecycle services across the
                mainstream endpoint, server, mobile, networking and peripherals
                ecosystem. Suntronix uses only officially approved OEM artwork
                inside the platform and does not imply authorisation where none
                exists.
              </p>
              <div>
                <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#0D1B3D] mb-2">
                  Endpoint & mobile
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {ENDPOINT_BRANDS.map((b) => (
                    <BrandTile key={b} label={b} tone="#2962FF" />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#0D1B3D] mb-2">
                  Networking & infrastructure
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {INFRA_BRANDS.map((b) => (
                    <BrandTile key={b} label={b} tone="#00C8FF" />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#0D1B3D] mb-2">
                  Peripherals & accessories
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {PERIPH_BRANDS.map((b) => (
                    <BrandTile key={b} label={b} tone="#18D5A4" />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-[#0D1B3D] mb-2">
                  Device management ecosystem
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {MDM_BRANDS.map((b) => (
                    <BrandTile key={b} label={b} tone="#7657FF" />
                  ))}
                </div>
              </div>
              <div className="text-[10px] text-[#8B95AB] leading-relaxed pt-2 border-t border-[#E1E7F0]">
                Brand names remain the property of their respective owners. Availability
                depends on customer agreement, geography and OEM allocation.
              </div>
            </div>
          )}
          {tab === "Contact" && (
            <div className="space-y-4">
              <h3 className="font-heading text-[20px] text-[#0D1B3D] tracking-[-0.01em]">SUNTRONIX</h3>
              <div className="text-[13px] text-[#4A5470]">Enterprise Technology Operations</div>
              <ul className="space-y-2.5 text-[13px] text-[#1E2A44]">
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#2962FF]" /> Chennai, India</li>
                <li className="flex items-center gap-2"><Globe className="h-4 w-4 text-[#2962FF]" /> www.suntronix.com</li>
                <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#2962FF]" /> hello@suntronix.com</li>
                <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#2962FF]" /> +91 (contact via website)</li>
              </ul>
              <div className="mt-4 text-[11px] text-[#8B95AB] leading-relaxed">
                For platform access, please continue with your work email on the sign-in panel.
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="rounded-md border border-[#E1E7F0] px-3 py-2">
      <div className="text-[10px] tracking-[0.16em] uppercase text-[#8B95AB]">{label}</div>
      <div className="text-[13px] font-medium text-[#0D1B3D] mt-0.5">{value}</div>
    </div>
  );
}

function BrandTile({ label, tone }) {
  return (
    <div className="rounded-md border border-[#E1E7F0] bg-white h-14 grid place-items-center relative overflow-hidden group hover:border-[#C6D2EA] transition-colors">
      <span aria-hidden className="absolute inset-y-0 left-0 w-0.5" style={{ background: tone }} />
      <span className="text-[12px] font-semibold text-[#0D1B3D] tracking-[-0.005em]">{label}</span>
    </div>
  );
}

const ENDPOINT_BRANDS = ["Apple", "Dell", "HP", "Lenovo", "Microsoft", "ASUS", "Acer", "Samsung", "OnePlus"];
const INFRA_BRANDS   = ["Cisco", "Aruba", "Juniper", "Ubiquiti", "Fortinet", "TP-Link"];
const PERIPH_BRANDS  = ["Logitech", "Jabra", "Poly", "Kingston", "Seagate", "Western Digital", "APC", "Anker", "Targus"];
const MDM_BRANDS     = ["Microsoft Intune", "Apple Business", "Google Workspace", "Jamf", "Kandji", "Windows Autopilot"];


const SERVICES = [
  { title: "Enterprise procurement",  body: "Multi-brand sourcing, customer catalogue, configuration and commercial control." },
  { title: "Deployment",                body: "Device configuration, Apple Business, Windows Autopilot, packing and deployment verification." },
  { title: "Manage",                     body: "Golden Asset Record, employee assignment, location, custody, warranty and lifecycle history." },
  { title: "Support",                    body: "Helpdesk, field support, repair, warranty / RMA, loaner and service-level tracking." },
  { title: "Recover",                    body: "Employee offboarding, reverse logistics, pickup, warehouse inward, inspection and chain of custody." },
  { title: "Reuse & redeploy",         body: "Reuse Before Buy, inventory matching, Device Fit, repair, redeployment and Technology Exchange." },
  { title: "ITAD & recycle",            body: "Secure data sanitisation, testing, grading, buyback, remarketing, responsible recycling and certificates." },
];
const POWERED = [
  { title: "One Golden Asset Record",  body: "Every technology asset maintains one permanent lifecycle history." },
  { title: "Complete chain of custody", body: "Track movement from procurement through deployment, support, recovery, reuse and final disposition." },
  { title: "Customer control",           body: "Customer, subsidiary, legal entity, country, location, employee and asset hierarchy." },
  { title: "Global visibility",           body: "One operational view across customers, locations, technology and lifecycle activity." },
  { title: "Connected operations",      body: "Integrations, events, workflow, courier, device management and operational intelligence." },
  { title: "Commercial control",         body: "Orders, billable events, service billing, reconciliation and customer invoicing." },
  { title: "Decision intelligence",      body: "Operational analytics, risk intelligence, reuse opportunities and Important Actions." },
];
